import React from 'react'
import axios from 'axios'
import { Button, Dialog } from '@material-ui/core'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'
import { BusinessManagerStage, toFunnelTemplateStages } from '@marketing-milk/interfaces'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import PresetActions from 'app/redux/actions/data/funnel/presets'
import { funnelTemplateService } from 'app/service/funnel-template'
import { isPerBusinessSource } from 'app/lib/funnel-template'
import Source from 'app/interface/data/funnel/source'

const DEFAULT_TEMPLATE_ICON = 'fas fa-layer-group'

const validationSchema = yup.object({
  name: yup.string().max(120).required('Template name is required'),
  desc: yup.string().max(500),
})

export interface SaveTemplateDialogProps {
  open: boolean
  onClose: () => void
  stages: BusinessManagerStage[]
  /** This business's describe list, where the per-business sources' names come from. */
  sources: Source[]
}

/**
 * Per-business sources carried by this funnel, with their names from this
 * business. They are saved as-is, since templates usually get re-applied within
 * the same account family, and the names are saved too so a business that
 * lacks them can say what it is missing.
 */
function perBusinessSources(
  stages: BusinessManagerStage[],
  sources: Source[]
): { key: string; name: string }[] {
  const names = new Map(sources.map(source => [source.meta.key, source.meta.name]))
  const keys = stages
    .flatMap(stage => stage.groups ?? [])
    .flatMap(group => group.sources ?? [])
    .filter(isPerBusinessSource)
  return Array.from(new Set(keys)).map(key => ({ key, name: names.get(key) ?? key }))
}

export function SaveTemplateDialog({ open, onClose, stages, sources }: SaveTemplateDialogProps) {
  const businessSpecific = perBusinessSources(stages, sources)

  const formik = useFormik({
    initialValues: { name: '', desc: '' },
    validationSchema,
    onSubmit: async values => {
      let normalizedStages
      try {
        // Normalized here rather than server-side only, so a funnel the schema
        // rejects surfaces as a form error instead of an opaque 400.
        normalizedStages = toFunnelTemplateStages(stages)
      } catch {
        formik.setStatus('This funnel has a stage that cannot be saved as a template.')
        return
      }

      try {
        const created = await funnelTemplateService.create({
          name: values.name,
          desc: values.desc,
          icon: DEFAULT_TEMPLATE_ICON,
          stages: normalizedStages,
          sourceNames: Object.fromEntries(
            businessSpecific
              .filter(source => source.name !== source.key)
              .map(source => [source.key, source.name])
          ),
        })
        // Appended locally rather than refetching — the card shows up at once.
        PresetActions.add(created)
        toast.success('Template saved')
        handleClose()
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          toast.error('You already have a template with that name')
        } else {
          toast.error('Unexpected error occurred while saving the template.')
        }
      }
    },
  })

  const canSubmit = !formik.isSubmitting && formik.isValid

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
      <DialogTitle>Save as Template</DialogTitle>
      <form className="form" noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <DialogContent>
          <DialogContentText>
            Save this funnel so you can apply it to any business you open.
          </DialogContentText>
          <TextField
            autoFocus
            className="mb-3"
            label="Name"
            fullWidth
            variant="standard"
            placeholder="e.g. Primrose Standard"
            InputLabelProps={{ shrink: true }}
            id="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            label="Description"
            fullWidth
            variant="standard"
            placeholder="e.g. Leads funnel for multi-location childcare"
            InputLabelProps={{ shrink: true }}
            className="pb-3"
            id="desc"
            name="desc"
            value={formik.values.desc}
            onChange={formik.handleChange}
            error={formik.touched.desc && Boolean(formik.errors.desc)}
            helperText={formik.touched.desc && formik.errors.desc}
          />
          {businessSpecific.length > 0 && (
            <div data-testid="per-business-source-notice" className="text-muted">
              <DialogContentText className="mb-1">
                {businessSpecific.length === 1 ? 'This data source is' : 'These data sources are'}{' '}
                specific to this business. They will be saved, but a business that doesn’t have them
                will ask to remove them when the template is applied:
              </DialogContentText>
              <ul className="pl-3">
                {businessSpecific.map(source => (
                  <li key={source.key}>
                    {source.name}
                    {source.key.startsWith('custom_conversions/') ? (
                      <span className="text-danger">
                        {' '}
                        (custom conversion, only works in businesses on this ad account)
                      </span>
                    ) : (
                      ' (insight)'
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {formik.status && <DialogContentText color="error">{formik.status}</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button disabled={!canSubmit} type="submit">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
